import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
  useLayoutEffect,
} from "react";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { makeStyles } from "@civicplus/preamble-ui/lib/Utilities/ThemeHelper";
import EventListTable from "./EventListTable";
import Loader from "@civicplus/preamble-ui/lib/Loader";
import { useSnackbar } from "notistack";
import { RootState, AppDispatch } from "../../app/store";
import {
  getEventsListAsync,
  cleanEventList,
  setLoading,
  setScrollToToday,
} from "../../actions";
import useTitle from "../../utils/setTitle";
import { scroller } from "react-scroll";
import Typography from "@civicplus/preamble-ui/lib/Typography";
import {
  convertUtcDateToLocalDateWithoutTime,
  scrollToDate,
} from "../../utils";
import { rowQuerySelector } from "./EventListRow";
import Grid from "@civicplus/preamble-ui/lib/Grid";
import { Fade } from "@material-ui/core";
import PopoverButton from "@civicplus/preamble-ui/lib/PopoverButton";
import InfoIcon from "mdi-material-ui/InformationSlabSymbol";
import { visuallyHidden } from "@mui/utils";

const useStyles = makeStyles(theme => ({
  tableWrapper: {
    container: "eventListContainer / inline-size",
    background: theme.palette.background.default,
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1,
    "-webkit-transform": "translate3d(0,0,0)",
    overflow: "auto",
    "&::-webkit-scrollbar-track": {
      backgroundColor: theme.palette.grey[300],
    },
    "&::-webkit-scrollbar": {
      width: 10,
    },
    "&::-webkit-scrollbar-thumb": {
      minHeight: "50px",
      backgroundColor: theme.palette.grey[500],
      borderRadius: theme.shape.borderRadius,
      "&:vertical": {
        height: 20,
        width: 20,
      },
      "&:hover": {
        backgroundColor: theme.palette.grey[600],
      },
    },
  },
  eventListTable: {
    padding: theme.spacing(1),
  },
  loadPastMessage: {
    paddingTop: theme.spacing(2),
  },
  loadMoreButton: {
    padding: theme.spacing(1, 2),
    margin: theme.spacing(1, "auto"),
    position: "relative",
    textAlign: "center",
    display: "block",
    border: "none",
  },
  infiniteScrollEnd: {
    [theme.breakpoints.up("xs")]: {
      height: "calc(100vh - 390px)",
    },
    [theme.breakpoints.up("md")]: {
      height: "calc(100vh - 410px)",
    },
    [theme.breakpoints.up("lg")]: {
      height: "calc(100vh - 440px)",
    },
  },
  loadMoreSpinner: {
    marginLeft: theme.spacing(1),
  },
  popoverButton: {
    maxWidth: "300px",
  },
  floatingButton: {
    position: "absolute",
    right: theme.spacing(2),
    transform: "translateY(-50%)",
    zIndex: theme.zIndex.appBar,
    boxShadow: theme.shadows[2],
    "&:hover,&[aria-expanded='true']": {
      animation: "none",
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
  },
}));

const resultsNumber = 15; // The default pagination number from the API to retrieve events

const EventList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const scrollToToday = useSelector((state: RootState) => state.scrollToToday);
  const isLoading = useSelector((state: RootState) => state.isLoading);
  const eventList = useSelector((state: RootState) => state.eventsList);
  const filters = useSelector((state: RootState) => state.eventFilters);
  const isNextLink = useSelector((state: RootState) => state.isNextLink);
  const isPastLink = useSelector((state: RootState) => state.isPastLink);
  const history = useHistory();
  const classes = useStyles();
  const initialLoad = useRef(true);
  const firstRender = useRef(true);
  const startScreen = useRef(null);
  const endScreen = useRef(null);
  const [loadingPastEvents, setLoadingPastEvents] = useState(false);
  const [loadingNextEvents, setLoadingNextEvents] = useState(false);
  const [pastMessage, setPastMessage] = useState("");
  const [upcomingMessage, setUpcomingMessage] = useState("");
  const prevLoadingPastEvents = useRef(false);
  const prevLoadingNextEvents = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const { setTitle } = useTitle();
  const [hasTriedLoadingPastEvents, setHasTriedLoadingPastEvents] =
    useState(false);
  const [noFutureEventsPublished, setNoFutureEventsPublished] = useState(false);
  const hasFetchedInitialEvents = useRef(false);
  const isDoingInitialFetch = eventList === null && isLoading;
  const tableEvents = isDoingInitialFetch ? null : eventList ?? [];

  const findFirstElementInViewPort = (elements: NodeListOf<Element>) =>
    Array.prototype.find.call(
      elements,
      element => element.getBoundingClientRect().y >= 85
    );
  const scrollTo = useMemo(() => {
    const events = document.querySelectorAll(rowQuerySelector);
    if (events) {
      return findFirstElementInViewPort(events);
    }
    return undefined;
    // We need this param to compute the last event only when this param is updated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventList]);

  const maintainScroll = useCallback(() => {
    if (scrollTo) {
      // When past date is refreshed it goes back to last date displayed once it is new dates are loaded.
      scroller.scrollTo(scrollTo.id, {
        duration: 0,
        containerId: "scroll-wrap",
        smooth: true,
        offset: -124,
      });
    }
  }, [scrollTo]);

  useEffect(() => {
    if (
      noFutureEventsPublished &&
      filters.categories?.length === 0 &&
      (!filters.date || (!filters.date.from && !filters.date.to))
    ) {
      const filter = {
        loadPastDate: {
          from: null,
          to: new Date().toISOString(),
        },
      };
      dispatch(setLoading(true));
      getEventsListAsync(
        filter,
        true,
        false,
        false
      )(dispatch)
        .then(() => {
          dispatch(setLoading(false));
          setNoFutureEventsPublished(false);
          const today = new Date();
          today.setHours(0, 0, 0);
          return scrollToDate(today);
        })
        .catch(() => {
          dispatch(setLoading(false));
          enqueueSnackbar(`Unable to load past events.`, {
            variant: "error",
          });
        });
    }
  }, [filters, noFutureEventsPublished, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (
      eventList === null ||
      (!filters.calendarDate &&
        !filters.date?.from &&
        !filters.date?.to &&
        !filters.categories)
    ) {
      hasFetchedInitialEvents.current = false;
    }
  }, [eventList, filters]);

  useEffect(() => {
    const containerEvents = document.querySelector("#scroll-wrap");
    if (
      containerEvents &&
      !loadingNextEvents &&
      !prevLoadingNextEvents.current
    ) {
      // Small offset to avoid trigger the infinite scroll for the past events after the page is reloaded
      containerEvents.scrollTop = 15;
    }
    if (eventList === null && !isLoading && !hasFetchedInitialEvents.current) {
      hasFetchedInitialEvents.current = true;
      setHasTriedLoadingPastEvents(false);

      dispatch(setLoading(true));
      getEventsListAsync(
        filters,
        false,
        false,
        true
      )(dispatch)
        .then(() => {
          return dispatch(setLoading(false));
        })
        .catch(() => {
          dispatch(setLoading(false));
          enqueueSnackbar(`Unable to load events.`, {
            variant: "error",
          });
        });
    } else if (
      eventList &&
      eventList.length === 0 &&
      !hasTriedLoadingPastEvents
    ) {
      if (!filters.date || (!filters.date.from && !filters.date.to)) {
        getEventsListAsync(
          filters,
          true,
          false,
          false
        )(dispatch)
          .then(() => {
            return setHasTriedLoadingPastEvents(true);
          })
          .catch(() => {
            return setHasTriedLoadingPastEvents(true);
          });
      } else {
        return setHasTriedLoadingPastEvents(true);
      }
    } else if (
      eventList &&
      eventList.length === 0 &&
      hasTriedLoadingPastEvents
    ) {
      setNoFutureEventsPublished(true);
    }
  }, [
    dispatch,
    enqueueSnackbar,
    eventList,
    filters,
    hasTriedLoadingPastEvents,
    isLoading,
    loadingNextEvents,
    loadingPastEvents,
    maintainScroll,
  ]);

  useEffect(() => {
    // handle the respective scroll effect
    if (
      !isLoading &&
      eventList &&
      eventList.length > 0 &&
      prevLoadingPastEvents.current === loadingPastEvents &&
      prevLoadingNextEvents.current === loadingNextEvents &&
      !loadingPastEvents &&
      !loadingNextEvents
    ) {
      if (!filters.date?.from && filters.date?.to) {
        // if the user filtered for to-date only, then we start from the bottom
        const endScreenEl = document.getElementById("endScreen");
        if (endScreenEl) {
          endScreenEl.scrollIntoView();
        }
      } else if (filters.date?.from) {
        // if the user filtered for from-date, then we start from the top
        const startScreenEl = document.getElementById(
          "Event-list-listSubheader-0"
        );
        if (startScreenEl) {
          startScreenEl.scrollIntoView();
        }
      } else {
        const li = document.getElementById("Event-list-listSubheader-0");
        const id = (li?.childNodes[0] as Element | null)?.id;
        if (
          initialLoad.current ||
          scrollToToday ||
          (filters &&
            !filters.date?.from &&
            !filters.date?.to &&
            !filters.calendarDate &&
            filters.categories)
        ) {
          initialLoad.current = false;
          const isSecondSubheaderExist = document.getElementById(
            "Event-list-listSubheader-1"
          );
          // scroll to hide Load Past Events button
          scroller.scrollTo(
            isSecondSubheaderExist
              ? "Event-list-listSubheader-1"
              : "Event-list-listSubheader-0",
            {
              duration: 500,
              containerId: "scroll-wrap",
              smooth: true,
              offset: isSecondSubheaderExist ? -100 : 0,
            }
          );
          if (scrollToToday) {
            dispatch(setScrollToToday(false));
          }
        } else {
          // scroll to the top
          scroller.scrollTo(id || "Event-list-listSubheader-0", {
            duration: 500,
            containerId: "scroll-wrap",
            smooth: true,
            offset: -(li?.offsetTop || 0),
          });
        }
      }
    } else if (prevLoadingPastEvents.current) {
      maintainScroll();
    }
    prevLoadingPastEvents.current = loadingPastEvents;
    prevLoadingNextEvents.current = loadingNextEvents;
    // we don't want to run this effect when the filters change
    // The scroll effect should only run one time when the event list is updated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    eventList,
    isLoading,
    loadingNextEvents,
    loadingPastEvents,
    maintainScroll,
    dispatch,
  ]);

  useEffect(() => {
    if (!firstRender.current) {
      if (!filters.search) {
        dispatch(cleanEventList());
      }
    } else {
      firstRender.current = false;
    }
  }, [dispatch, filters, history]);

  useEffect(() => {
    setTitle("Events");
  }, [setTitle]);

  const loadPastEvents = useCallback(
    (tabNavigation = false) => {
      setPastMessage("");
      if (!loadingPastEvents) {
        if (filters.date?.from && eventList && eventList?.length > 0) {
          setPastMessage("There are no past events for your selected filters");
          return;
        } else if (isPastLink && eventList && eventList?.length > 0) {
          setPastMessage("There are no more past events");
          return;
        }

        const currEvents = document.querySelectorAll(
          "#event-list-table li>*[role='button']"
        );
        const firstEvent = currEvents[0];
        if (firstEvent) {
          const firstEventId = firstEvent.getAttribute("id");
          const endDate = filters.calendarDate
            ? new Date(filters.calendarDate)
            : new Date();
          endDate.setHours(0, 0, 0);

          // Calculate OData $skip value from current event list.
          let pastEventCount = 0;

          for (const event of currEvents) {
            const eventDate = event.getAttribute("data-date");
            // This check is necessary to remove duplicates.
            const eventId = event.getAttribute("data-id");

            if (eventDate && eventId) {
              if (new Date(eventDate) < endDate) {
                pastEventCount++;
              }
            }
          }
          const filter = {
            ...filters,
            loadPastDate: {
              from: filters.date?.from || null,
              to: endDate.toISOString(),
              skip: pastEventCount,
            },
          };

          setLoadingPastEvents(true);
          getEventsListAsync(
            filter,
            true,
            false,
            false
          )(dispatch)
            .then(() => {
              setLoadingPastEvents(false);
              if (tabNavigation) {
                const previousEvent = firstEventId
                  ? document.getElementById(firstEventId)?.parentElement
                      ?.previousElementSibling?.firstElementChild
                  : null;
                if (previousEvent) (previousEvent as HTMLElement)?.focus();
              }
              return;
            })
            .catch(() => {
              // Show error
              enqueueSnackbar(`Unable to load past events.`, {
                variant: "error",
              });
            });
        }
      }
    },
    [
      dispatch,
      enqueueSnackbar,
      eventList,
      filters,
      isPastLink,
      loadingPastEvents,
    ]
  );

  const loadNextEvents = useCallback(
    (tabNavigation = false) => {
      if (!loadingNextEvents) {
        setUpcomingMessage("");
        const currEvents = document.querySelectorAll(
          "#event-list-table li>*[role='button']"
        );
        const firstDate = eventList && eventList[0].startDateTime;

        // If the date filters are set, we prioritize those.
        const startDate = filters.date?.from
          ? new Date(filters.date.from)
          : filters.calendarDate
          ? new Date(filters.calendarDate)
          : new Date();
        startDate.setUTCHours(0, 0, 0);

        const endDate = filters.date?.to ? new Date(filters.date.to) : null;
        if (endDate instanceof Date && !isNaN(endDate.getTime())) {
          // Since this is the endDate, we want to include all events on that date.
          endDate.setUTCHours(23, 59, 59);
        }

        const latestEvent = currEvents[currEvents.length - 1];
        const latestEventDate = latestEvent.getAttribute("data-date") || "";
        const latestEventDateFormat =
          convertUtcDateToLocalDateWithoutTime(latestEventDate);
        if (filters.date?.to && !isNextLink) {
          setUpcomingMessage(
            "There are no upcoming events for your selected filters"
          );
          return;
        } else if (!isNextLink) {
          if (latestEventDateFormat) {
            if (latestEventDateFormat >= new Date()) {
              setUpcomingMessage("There are no more upcoming events");
            } else {
              setUpcomingMessage("No current or future events to display");
            }
          }
          return;
        }

        let currentEventsLoadedCount = 0;

        if (eventList && eventList.length > 0) {
          if (firstDate) {
            // We need to check how many events in the current list
            // match the date ranges being sent to the API to get an
            // accurate $skip value.
            for (const event of currEvents) {
              const eventDate = event.getAttribute("data-date");
              const eventId = event.getAttribute("data-id");

              // Checking eventId can remove duplicates sometimes seen in the list.
              if (eventDate && eventId) {
                const eventDateObj = new Date(eventDate);
                if (
                  startDate <= eventDateObj &&
                  (!endDate || eventDateObj <= endDate)
                ) {
                  currentEventsLoadedCount++;
                }
              }
            }
          }
        }

        const filter = {
          ...filters,
          skip: currentEventsLoadedCount,
          date: {
            from: startDate.toISOString(),
            to: filters.date?.to || null,
          },
        };

        setLoadingNextEvents(true);
        getEventsListAsync(
          filter,
          false,
          true,
          false
        )(dispatch)
          .then(() => {
            setLoadingNextEvents(false);
            if (tabNavigation && latestEvent) {
              const latestEventId = latestEvent.getAttribute("id");
              const nextEvent = latestEventId
                ? document.getElementById(latestEventId)?.parentElement
                    ?.nextElementSibling?.firstElementChild
                : null;
              if (nextEvent) (nextEvent as HTMLElement)?.focus();
            }
            return;
          })
          .catch(() => {
            // Show error
            enqueueSnackbar(`Unable to load new events.`, {
              variant: "error",
            });
          });
      }
    },
    [
      loadingNextEvents,
      eventList,
      filters,
      isNextLink,
      dispatch,
      enqueueSnackbar,
    ]
  );

  useEffect(() => {
    setUpcomingMessage("");
    setPastMessage("");
  }, [filters]);

  const handleObserverStartScreen = useCallback(
    entries => {
      const targetEl = entries[0];
      let hasListFocus = false;
      // Check if the event list has focus
      if (document.hasFocus()) {
        const list = document.getElementById("scroll-wrap");
        const listButtons = list?.querySelectorAll(
          "#startScreen, li>*[role='button'], #endScreen"
        );
        listButtons?.forEach(button => {
          if (button === document.activeElement) {
            hasListFocus = true;
          }
        });
      }
      if (targetEl.isIntersecting && !hasListFocus) {
        loadPastEvents();
      }
    },
    [loadPastEvents]
  );

  const handleObserverEndScreen = useCallback(
    entries => {
      const targetEl = entries[0];
      let hasListFocus = false;
      // Check if the event list has focus
      if (document.hasFocus()) {
        const list = document.getElementById("scroll-wrap");
        const listButtons = list?.querySelectorAll(
          "#startScreen, li>*[role='button'], #endScreen"
        );
        listButtons?.forEach(button => {
          if (button === document.activeElement) {
            hasListFocus = true;
          }
        });
      }
      if (targetEl.isIntersecting && !hasListFocus) {
        loadNextEvents();
      }
    },
    [loadNextEvents]
  );
  const observerOption = useMemo(
    () => ({
      root: null,
      rootMargin: "100px",
      threshold: 0.9,
    }),
    []
  );

  useLayoutEffect(() => {
    if (!eventList || eventList.length < 3 || !hasFetchedInitialEvents.current)
      return;

    // Observers to handle infinite scroll
    const observerStart = new IntersectionObserver(
      handleObserverStartScreen,
      observerOption
    );
    if (startScreen.current) {
      observerStart.observe(startScreen.current);
    }
    const observerEnd = new IntersectionObserver(
      handleObserverEndScreen,
      observerOption
    );
    if (eventList && eventList.length >= resultsNumber) {
      if (endScreen.current) {
        observerEnd.observe(endScreen.current);
      }
    }

    return () => {
      observerStart.disconnect();
      observerEnd.disconnect();
    };
  }, [
    eventList,
    handleObserverStartScreen,
    handleObserverEndScreen,
    observerOption,
    loadNextEvents,
  ]);

  const infiniteScroll = (show: boolean, previous = false) => {
    if (eventList && eventList.length > 0) {
      const focusHelperId = previous
        ? "focusHelperMessagePrevious"
        : "focusHelperMessageNext";

      return (
        <Grid container={true} spacing={1} alignItems="center">
          <Grid item={true} xs="auto">
            <Typography variant="body1" role={show ? "alert" : undefined}>
              {`${show ? "Loading" : "Load"} more ${
                previous ? "previous" : "upcoming"
              } events`}
            </Typography>
            <Typography
              style={visuallyHidden}
              id={focusHelperId}
              aria-hidden={true}
            >
              {`${
                previous
                  ? "You will be inserted to the previous event from the top of the current list, after loading"
                  : "You will be inserted to the next event from the bottom of the current list, after loading"
              }`}
            </Typography>
          </Grid>
          <Grid item={true} xs="auto">
            <Fade in={show} unmountOnExit={true}>
              <Loader
                verticallyCenter={true}
                className={classes.loadMoreSpinner}
                size={20}
                aria-hidden={true}
                aria-label="Loading spinner"
              />
            </Fade>
          </Grid>
        </Grid>
      );
    }
    return <></>;
  };
  return (
    <>
      <PopoverButton
        id="scroll-helper"
        className={classes.floatingButton}
        popoverText={
          <>
            <Typography variant="body2" gutterBottom={true}>
              Navigate the list of events with the scroll bar
            </Typography>
            <Typography variant="caption" display="block">
              • Scroll up to see past events.
            </Typography>
            <Typography variant="caption" display="block">
              • Scroll down to see upcoming events.
            </Typography>
          </>
        }
        customHelperIcon={<InfoIcon />}
        type="fab"
        PopoverProps={{
          classes: {
            paper: classes.popoverButton,
          },
        }}
      />
      <Grid
        container={true}
        justifyContent="center"
        className={classes.tableWrapper}
        id="scroll-wrap"
        role="application"
        aria-describedby="Event-list"
      >
        {eventList?.length !== 0 && (
          <Grid item={true} xs={12}>
            {pastMessage ? (
              <Typography
                variant="body1"
                align="center"
                role="alert"
                className={classes.loadPastMessage}
              >
                {pastMessage}
              </Typography>
            ) : (
              <button
                id="startScreen"
                ref={startScreen}
                className={classes.loadMoreButton}
                onClick={() => loadPastEvents(true)}
                disabled={loadingPastEvents}
                aria-describedby="focusHelperMessagePrevious"
                aria-label="Load more previous events"
              >
                {infiniteScroll(loadingPastEvents, true)}
              </button>
            )}
          </Grid>
        )}
        <Grid
          item={true}
          xs={12}
          id="event-list-table"
          className={classes.eventListTable}
        >
          <EventListTable
            events={tableEvents}
            loading={loadingPastEvents || loadingNextEvents}
          />
        </Grid>
        {eventList?.length !== 0 && (
          <Grid item={true}>
            {upcomingMessage ? (
              <Typography
                variant="body1"
                align="center"
                role="alert"
                className={classNames({
                  [classes.infiniteScrollEnd]: upcomingMessage,
                })}
              >
                {upcomingMessage}
              </Typography>
            ) : (
              <button
                id="endScreen"
                ref={endScreen}
                className={classes.loadMoreButton}
                onClick={() => loadNextEvents(true)}
                disabled={loadingNextEvents}
                aria-describedby="focusHelperMessageNext"
                aria-label="Load more upcoming events"
              >
                {infiniteScroll(loadingNextEvents)}
              </button>
            )}
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default EventList;
